import { createSlice } from '@reduxjs/toolkit';

import { AppMessageSeverities } from '@/types';

import type { AppMessage } from '@/types';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface AppState {
  chatId: string;
  appMessage: AppMessage;
  momentsGeneration: number;
  momentsSubject: string;
  momentsAuthRealm: string;
  momentsReadinessUnauthorized: boolean;
}

const DEFAULT_APP_MESSAGE: AppMessage = {
  message: '',
  open: false,
  severity: AppMessageSeverities.Success,
};

const initialState: AppState = {
  chatId: '',
  appMessage: DEFAULT_APP_MESSAGE,
  momentsGeneration: 0,
  momentsSubject: '',
  momentsAuthRealm: '',
  momentsReadinessUnauthorized: false,
};

const appSlice = createSlice({
  initialState,
  name: 'app',
  reducers: {
    // One purge per unauthorized Moments episode. Preserve valid Rails identity
    // so reloading it cannot turn a terminal Moments 401 into a reset loop.
    failMomentsReadiness: (state) =>
      state.momentsReadinessUnauthorized
        ? state
        : {
            ...state,
            momentsGeneration: state.momentsGeneration + 1,
            momentsSubject: '',
            momentsReadinessUnauthorized: true,
          },
    restoreMomentsReadiness: (state) => ({
      ...state,
      momentsReadinessUnauthorized: false,
    }),
    clearMomentsPrivateState: (state) => ({
      ...state,
      momentsGeneration: state.momentsGeneration + 1,
      momentsSubject: '',
      momentsAuthRealm: '',
      momentsReadinessUnauthorized: false,
    }),
    clearMomentsPrivateStateFromOtherTab: (state) => ({
      ...state,
      momentsGeneration: state.momentsGeneration + 1,
      momentsSubject: '',
      momentsAuthRealm: '',
      // Keep an already-terminal failure episode terminal across nonce echoes.
      // A successful readiness response or explicit local logout resets it.
    }),
    observeMomentsAuthRealm: (state, action: PayloadAction<string>) => ({
      ...state,
      momentsGeneration:
        state.momentsAuthRealm && state.momentsAuthRealm !== action.payload
          ? state.momentsGeneration + 1
          : state.momentsGeneration,
      momentsReadinessUnauthorized:
        state.momentsAuthRealm && state.momentsAuthRealm !== action.payload
          ? false
          : state.momentsReadinessUnauthorized,
      momentsAuthRealm: action.payload,
      momentsSubject:
        state.momentsAuthRealm && state.momentsAuthRealm !== action.payload
          ? ''
          : state.momentsSubject,
    }),
    observeMomentsSubject: (state, action: PayloadAction<string>) => ({
      ...state,
      momentsGeneration:
        state.momentsSubject && state.momentsSubject !== action.payload
          ? state.momentsGeneration + 1
          : state.momentsGeneration,
      momentsSubject: action.payload,
    }),
    setChatId: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.chatId = action.payload;
    },

    setAppMessage: (state, action: PayloadAction<AppMessage>) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.appMessage = action.payload;
    },
    clearAppMessage: (state) => {
      // eslint-disable-next-line no-param-reassign -- Redux Toolkit uses Immer which allows safe mutations
      state.appMessage = DEFAULT_APP_MESSAGE;
    },
  },
});

export const {
  setChatId,
  setAppMessage,
  clearAppMessage,
  clearMomentsPrivateState,
  failMomentsReadiness,
  restoreMomentsReadiness,
  clearMomentsPrivateStateFromOtherTab,
  observeMomentsSubject,
  observeMomentsAuthRealm,
} = appSlice.actions;

export default appSlice.reducer;
